export default {
  common: {
    form: {
      moreOptions: 'More options',
      moreOptionsCaption: 'Extra details you can fill in. All optional.',
    },
    loading: 'Loading…',
    retry: 'Retry',
    cancel: 'Cancel',
    save: 'Save',
    delete: 'Delete',
    edit: 'Edit',
    create: 'Create',
    reset: 'Reset',
    showing: 'Showing {from} to {to} of {total} {entity}',
    noResults: 'No {entity} found',
    system: 'System',
    viewDetails: 'View details',
    actions: 'Actions',
    yes: 'Yes',
    no: 'No',
    action: {
      cancel: 'Cancel',
      refresh: 'Refresh',
      close: 'Close',
      search: 'Search',
      reset: 'Reset',
      goHome: 'Go Home',
      back: 'Back',
      save: 'Save',
    },
    filters: {
      perPage: 'Per Page',
      activeFilters: 'Active Filters:',
      search: 'Search...',
      clearSearch: 'Clear search',
      toggle: 'Filters',
      active: 'Filters active',
      itemsPerPage: 'Items per page',
      searchAndFilters: 'Search & filters',
      hide: 'Hide search & filters',
      toggleWithCount: 'Filters, {count} active',
      searchAndFiltersWithCount: 'Search & filters, {count} active',
    },
    error: {
      unableToConnectToServer: 'Unable to connect to server. Please check your internet connection.',
      pageLoadFailed: 'This page could not be loaded. Refresh to try again.',
      requiredPermission: 'Required permission: {slug}',
      requiredPermissionWithRef: 'Required permission: {slug} · {ref}',
      errorReference: 'Reference: {ref}',
    },
    auth: {
      emailNotVerified: 'Your email address has not been verified.',
      resendVerification: 'Resend verification email',
      sessionExpired: 'Your session has expired. Please sign in again.',
      invalidCredentials: 'The email or password you entered is incorrect.',
      invalidGrant: 'This sign-in link is no longer valid. Please sign in again.',
      accessDenied: 'You don\'t have permission to perform this action.',
      accountSuspended: 'Your account has been suspended. Please contact support if you believe this is a mistake.',
      csrfMismatch: 'Your session is out of sync. Please refresh the page and try again.',
      authError: 'Authentication failed. Please try again.',
      login: {
        title: 'Staff Portal',
        subtitle: 'Sign in to continue',
        brandTagline: 'Staff Administration Console',
        emailLabel: 'Email',
        passwordLabel: 'Password',
        submit: 'Sign in',
        welcome: 'Welcome back!',
        errors: {
          missingCredentials: 'Please enter email and password',
          invalidCredentials: 'Sign-in failed. Please check your credentials.',
          profileLoadFailed: 'Failed to load user profile',
          unexpected: 'An unexpected error occurred. Please try again.',
        },
      },
      unauthorized: {
        title: 'Access Denied',
        description: "You don't have permission to access this page.",
        staffOnly: 'This area is restricted to staff members only.',
        returnToLogin: 'Return to sign in',
      },
      logout: {
        success: 'Successfully signed out',
        failed: 'Failed to sign out. Please try again.',
        signOut: 'Sign Out',
      },
      twoFactor: {
        emailInstruction: 'Enter the 6-digit code we emailed you.',
        totpInstruction: 'Enter the 6-digit code from your authenticator app.',
        smsInstruction: 'Enter the 6-digit code we sent you by SMS.',
        codeLabel: 'Authentication code',
        verify: 'Verify',
        resend: 'Resend code',
        back: 'Back to sign in',
        resent: 'A new code has been sent.',
        verificationFailed: 'Verification failed. Please try again.',
        attemptsRemaining: '{n} attempt remaining | {n} attempts remaining',
      },
    },
    content: {
      loading: 'Loading content...',
      errorTitle: 'Content Unavailable',
      errorMessage: "We couldn't load this content. Please try again.",
      retry: 'Try Again',
      errorHint: 'Check your connection or tap below to try again.',
    },
    dialog: {
      delete: {
        title: 'Confirm Deletion',
        message: 'This action cannot be undone. The item will be permanently removed.',
        confirmButton: 'Delete',
        confirmWord: 'delete',
      },
    },
    errors: {
      validationFailed: 'Validation failed',
      notFound: 'Oops. Nothing here...',
      actionFailed: 'Action failed. Please try again.',
    },
    fallback: {
      unknown: 'Unknown',
    },
    copy: 'Copy',
    copied: 'Copied',
    avatar: 'Avatar',
    notSet: 'Not set',
    accessibility: {
      loading: 'Loading',
      skipToContent: 'Skip to main content',
    },
    field: {
      required: '(required)',
      optional: '(optional)',
      status: 'Status',
    },
    media: {
      categories: {
        all: 'All',
        image: 'Image',
        video: 'Video',
        audio: 'Audio',
        document: 'Document',
        pdf: 'PDF',
        other: 'Other',
      },
      filter: {
        searchPlaceholder: 'Search files...',
      },
      lightbox: {
        closeTooltip: 'Close (Esc)',
        previousTooltip: 'Previous (←)',
        nextTooltip: 'Next (→)',
        unsupportedPreview: 'This file type cannot be previewed',
        downloadFile: 'Download File',
        retryPreview: 'Try Again',
        playbackNotReady: 'This file is still being prepared for playback. Try again shortly.',
      },
      actions: {
        download: 'Download',
      },
    },
  },
  nav: {
    brand: 'Staff Portal',
    collapse: 'Collapse',
    openMenu: 'Open navigation menu',
    realtime: {
      disconnected: 'Realtime disconnected',
      disconnectedHint: 'Live updates are paused. Reload the page to reconnect.',
    },
    navigation: 'Main navigation',
    lightMode: 'Light Mode',
    darkMode: 'Dark Mode',
    version: {
      title: 'Version information',
      loading: 'Loading API version...',
      environment: 'Environment',
      si: 'SI',
      api: 'API',
      built: 'Built',
      commit: 'Commit',
      buildFormat: '{version} (build {build})',
      loadError: 'Failed to load version information',
    },
    userMenu: {
      fallbackName: 'Staff User',
    },
    sections: {
      support: 'Support',
      management: 'Management',
      administration: 'Administration',
      tenantData: 'Tenant Data',
    },
    items: {
      properties: 'Properties',
      projects: 'Projects',
      tasks: 'Tasks',
      teams: 'Teams',
      schedules: 'Schedules',
      inventory: 'Inventory',
      timeTracking: 'Time Tracking',
      billing: 'Billing',
      dashboard: 'Dashboard',
      tickets: 'Tickets',
      users: 'Users',
      workspaces: 'Workspaces',
      ads: 'Advertisements',
      advertisers: 'Advertisers',
      marketingTemplates: 'Email Templates',
      marketplaceLeads: 'Marketplace Leads',
      acquisition: 'Acquisition',
      audit: 'Audit Logs',
      clientLogs: 'Client Logs',
      clientLogPolicies: 'Logging Policies',
      exports: 'Exports',
      legalDocuments: 'Legal Documents',
      settings: 'System Settings',
      moderation: 'Moderation',
      messageReports: 'Message Reports',
      mlPredictions: 'ML Prediction Review',
      mlModels: 'ML Models',
      mlFeedback: 'ML Feedback',
      notificationMetrics: 'Notification Metrics',
      deletionRequests: 'Deletion Requests',
      maintenance: 'Maintenance Mode',
      supportChat: 'Support Chat',
      knowledgeManagement: 'Knowledge Management',
      featureFlags: 'Feature Flags',
      conversations: 'Messaging Oversight',
      activity: 'Activity Feed',
      sentEmails: 'Sent Emails',
    },
  },
  dashboard: {
    title: 'Dashboard',
    welcome: 'Welcome back, {name}',
    autoRefresh: 'Auto-refresh',
    subtitle: "Here's what's happening in your staff portal today.",
    updatedAt: '(Updated at {time})',
    loadFailed: 'Failed to load dashboard data',
    pleaseRetry: 'Please try again',
    stats: {
      openTickets: 'Open Tickets',
      activeUsers: 'Active Users',
      workspaces: 'Workspaces',
      pendingActions: 'Pending Actions',
      moderationBacklog: 'Moderation Backlog',
      invoicesPeriod: 'Invoices (this period)',
    },
    recentActivity: {
      title: 'Recent Activity',
      empty: 'No recent activity',
      emptyCaption: 'Activity will appear here as you work',
    },
    priorityQueue: {
      title: 'Needs Attention',
      empty: 'No urgent tickets',
      seeAll: 'See all urgent tickets',
      closeConfirm: 'Close this ticket?',
    },
    systemStatus: {
      title: 'System Status',
      operational: 'All Systems Operational',
      degraded: 'Degraded',
      maintenance: 'Maintenance',
      unreachable: 'API Unreachable',
      checking: 'Checking…',
    },
    time: {
      justNow: 'just now',
      minAgo: '{n} min ago',
      hoursAgo: '{n} hour ago | {n} hours ago',
      daysAgo: '{n} day ago | {n} days ago',
    },
  },
  users: {
    searchPlaceholder: 'Search users by email, phone, or name...',
    loadFailed: 'Failed to load users',
    createDialog: {
      create: 'Create user',
      title: 'Create User',
      name: 'Name',
      email: 'Email',
      password: 'Password',
      passwordConfirm: 'Confirm password',
      success: 'User created',
      failed: 'Failed to create user',
    },
    filters: {
      role: 'Role',
      emailStatus: 'Email Status',
      phoneStatus: 'Phone Status',
    },
    roles: {
      all: 'All Roles',
      staff: 'Staff',
      admin: 'Admin',
      user: 'User',
      guest: 'Guest',
    },
    verified: {
      all: 'All',
      verified: 'Verified',
      notVerified: 'Not Verified',
    },
    columns: {
      nameContact: 'Name & Contact',
      role: 'Role',
      verification: 'Verification',
      created: 'Created',
    },
    tooltips: {
      emailVerified: 'Email verified',
      emailNotVerified: 'Email not verified',
      phoneVerified: 'Phone verified',
      phoneNotVerified: 'Phone not verified',
    },
    detail: {
      title: 'User Details',
      loading: 'Loading user details...',
      loadError: 'Failed to load user details',
      sectionAccount: 'Account Information',
      sectionWorkspaces: 'Workspaces ({count})',
      sectionPermissions: 'System Permissions ({count})',
      emptyWorkspaces: 'No workspaces',
      emptyPermissions: 'No system permissions',
      suspendedBadge: 'Suspended',
      fields: {
        uuid: 'UUID',
        emailVerified: 'Email Verified',
        notVerified: 'Not verified',
        created: 'Created',
        lastUpdated: 'Last Updated',
        suspendedAt: 'Suspended',
        suspensionReason: 'Suspension reason',
      },
      actions: {
        edit: 'Edit',
        suspend: 'Suspend',
        unsuspend: 'Unsuspend',
        permissions: 'Permissions',
        delete: 'Delete',
      },
      editDialog: {
        title: 'Edit User',
        nameLabel: 'Name',
        emailLabel: 'Email',
        phoneLabel: 'Phone',
        systemRoleLabel: 'System Role',
      },
      suspendDialog: {
        title: 'Suspend User',
        reasonLabel: 'Reason for suspension',
        submit: 'Suspend',
      },
      deleteDialog: {
        title: 'Delete User',
        message: 'This permanently deletes the user. Type {email} to confirm.',
      },
      permissionsDialog: {
        addAriaLabel: 'Add permission',
        title: 'Manage Permissions',
        slugLabel: 'Permission slug to add',
        slugHint: 'e.g. staff.tickets.view',
        toAddCaption: 'Permissions to add:',
        toRemoveCaption: 'Permissions to remove:',
        currentCaption: 'Current permissions (click to remove):',
        submit: 'Apply Changes',
      },
      debugLogging: {
        sectionTitle: 'Debug Logging',
        statusActive: 'Active',
        statusExpired: 'Expired',
        empty: 'No active debug logging policy for this user',
        fields: {
          level: 'Minimum level',
          expires: 'Expires',
          reason: 'Reason',
          sampleRate: 'Sampling',
        },
        actions: {
          enable: 'Enable logging',
          replace: 'Replace policy',
          disable: 'Stop logging',
        },
        enableDialog: {
          title: 'Enable Remote Debug Logging',
          levelLabel: 'Minimum level to capture',
          durationLabel: 'Duration',
          durations: {
            h1: '1 hour',
            h4: '4 hours',
            h24: '24 hours',
            h72: '3 days',
            d7: '7 days',
            d30: '30 days',
          },
          reasonLabel: 'Reason (required, audited)',
          advanced: 'Advanced',
          sampleRateLabel: 'Sample rate',
          sampleRateHint: 'Rates below 100% also drop error events. Keep at 100% when hunting a specific bug.',
          submit: 'Enable',
        },
        disableDialog: {
          title: 'Stop Remote Debug Logging',
          reasonLabel: 'Reason (required, audited)',
          submit: 'Stop logging',
        },
        toasts: {
          enabled: 'Remote debug logging enabled',
          enableFailed: 'Failed to enable debug logging',
          disabled: 'Remote debug logging stopped',
          disableFailed: 'Failed to stop debug logging',
        },
      },
      toasts: {
        updated: 'User updated',
        updateFailed: 'Failed to update user',
        suspended: 'User suspended',
        suspendFailed: 'Failed to suspend user',
        restored: 'User restored',
        restoreFailed: 'Failed to restore user',
        deleted: 'User deleted',
        deleteFailed: 'Failed to delete user',
        permissionsUpdated: 'Permissions updated',
        permissionsFailed: 'Failed to update permissions',
      },
    },
  },
  tickets: {
    title: 'Support Tickets',
    searchPlaceholder: 'Search by subject or UUID...',
    tabs: {
      all: 'All',
      open: 'Open',
      inProgress: 'In Progress',
      waiting: 'Waiting',
      resolved: 'Resolved',
      closed: 'Closed',
      pendingVerification: 'Pending Verification',
    },
    filters: {
      priority: 'Priority',
      assignment: 'Assignment',
      channel: 'Channel',
      dateFrom: 'From date',
      dateTo: 'To date',
    },
    channels: {
      all: 'All Channels',
      web_form: 'Web Form',
      email: 'Email',
      chat: 'Chat',
      phone: 'Phone',
      api: 'API',
    },
    types: {
      contact: 'Contact',
      support: 'Support',
      incident: 'Incident',
      feature: 'Feature Request',
    },
    priorities: {
      all: 'All Priorities',
      low: 'Low',
      normal: 'Normal',
      high: 'High',
      urgent: 'Urgent',
    },
    assignments: {
      all: 'All Assignments',
      unassigned: 'Unassigned',
      assignedToMe: 'Assigned to me',
    },
    statuses: {
      in_progress: 'In Progress',
      waiting: 'Waiting',
      resolved: 'Resolved',
    },
    realtime: {
      newTicket: 'New ticket: {subject}',
      priorityCaption: 'Priority: {priority}',
    },
    stats: {
      open: 'Open',
      pendingAction: 'Pending action',
      total: 'Total',
      newThisPeriod: 'New ({period})',
    },
    card: {
      messagesCount: 'no messages | one message | {count} messages',
      assignedTo: 'Assigned to',
      unassigned: 'Unassigned',
      dateToday: 'Today',
      dateYesterday: 'Yesterday',
      dateDaysAgo: '{count} day ago | {count} days ago',
      slaDue: 'SLA due {date}',
      slaOverdue: 'SLA overdue',
    },
    detail: {
      title: 'Ticket Details',
      loading: 'Loading ticket details...',
      loadError: 'Failed to load ticket details',
      fields: {
        assignedTo: 'Assigned to',
        workspace: 'Workspace',
        created: 'Created',
        closed: 'Closed',
        issueType: 'Issue type',
        channel: 'Channel',
        slaDue: 'SLA deadline',
        tags: 'Tags',
        reference: 'Reference',
      },
      attachments: {
        download: 'Download',
        downloadFailed: 'Failed to download attachment',
      },
      actions: {
        respond: 'Respond',
        assign: 'Assign',
        escalate: 'Escalate',
        changeStatus: 'Change Status',
        reopen: 'Reopen',
        close: 'Close',
        delete: 'Delete',
        editDetails: 'Edit',
        resendVerification: 'Resend verification',
      },
      messages: {
        sectionTitle: 'Messages ({count})',
        empty: 'No messages yet',
        internalNote: 'Internal note',
      },
      createDialog: {
        title: 'Create Ticket',
        userLabel: 'User',
        noUser: 'No users found',
        subjectLabel: 'Subject',
        issueTypeLabel: 'Issue type',
        priorityLabel: 'Priority',
        messageLabel: 'Initial message',
        submit: 'Create Ticket',
        success: 'Ticket created',
        failed: 'Failed to create ticket',
      },
      respondDialog: {
        title: 'Respond to Ticket',
        messageLabel: 'Message',
        attachmentsLabel: 'Attachments (optional)',
        attachmentsHint: 'Up to 3 files, 16 MB each. Images and documents only.',
        attachmentsRejected: 'Some files were rejected: check the count, size and file type.',
        internalNoteLabel: 'Internal note (not visible to user)',
        submitReply: 'Send Reply',
        submitNote: 'Add Note',
        emailWillBeSent: 'Email will be sent to requestor',
        internalNoteNotice: 'Not visible to requestor',
      },
      assignDialog: {
        title: 'Assign Ticket',
        staffLabel: 'Staff Member',
        noStaff: 'No staff members found',
        assignToMe: 'Assign to me',
        submit: 'Assign',
      },
      changeStatusDialog: {
        title: 'Change Status',
        submit: 'Change Status',
      },
      escalateDialog: {
        title: 'Escalate Ticket',
        priorityLabel: 'New Priority',
        submit: 'Escalate',
      },
      closeDialog: {
        title: 'Close Ticket',
        notesLabel: 'Closing notes (optional)',
        submit: 'Close Ticket',
      },
      deleteDialog: {
        title: 'Delete Ticket',
        message: 'This permanently deletes the ticket and its messages. This cannot be undone. Continue?',
      },
      resendVerificationDialog: {
        title: 'Resend verification email',
        message: 'This sends a fresh verification email to the submitter\'s address. Continue?',
      },
      editDialog: {
        title: 'Edit Ticket',
        subjectLabel: 'Subject',
        issueTypeLabel: 'Issue type',
      },
      toasts: {
        responseSent: 'Response sent',
        responseFailed: 'Failed to send response',
        assigned: 'Ticket assigned',
        assignFailed: 'Failed to assign ticket',
        staffLoadFailed: 'Failed to load staff list',
        priorityUpdated: 'Ticket priority updated',
        escalateFailed: 'Failed to escalate ticket',
        statusChanged: 'Status updated',
        statusChangeFailed: 'Failed to update status',
        closed: 'Ticket closed',
        closeFailed: 'Failed to close ticket',
        deleted: 'Ticket deleted',
        deleteFailed: 'Failed to delete ticket',
        updated: 'Ticket updated',
        updateFailed: 'Failed to update ticket',
        verificationResent: 'Verification email sent',
        verificationResendFailed: 'Failed to resend verification email',
      },
    },
  },
  workspaces: {
    approval: {
      title: 'Time-tracking approval',
      modeLabel: 'Approval routing',
      mode: {
        pool: 'Anyone with approval rights',
        designated: 'Designated approver (with fallback)',
        designatedStrict: 'Designated approver only',
      },
      modeHint: {
        pool: 'Any owner, admin, or lead can approve any member\'s time.',
        designated: 'A member\'s designated approver reviews their time; without one, anyone with approval rights can.',
        designatedStrict: 'Only the designated approver, their backup, or an admin can approve. No general pool.',
      },
      forbidSelf: 'Prevent owners and admins from approving their own time',
      attestation: 'Require members to confirm accuracy when submitting',
      save: 'Save policy',
      saved: 'Approval policy updated',
      saveError: 'Failed to update the approval policy',
      loadError: 'Could not load the approval settings',
      membersTitle: 'Designated approvers',
      approverLabel: 'Approver',
      memberSaved: 'Approver updated',
      memberSaveError: 'Failed to update the approver',
    },
    title: 'Workspaces',
    searchPlaceholder: 'Search workspaces by name...',
    loadFailed: 'Failed to load workspaces',
    filters: {
      status: 'Status',
    },
    statuses: {
      all: 'All Statuses',
      active: 'Active',
      inactive: 'Inactive',
      suspended: 'Suspended',
    },
    stats: {
      members: 'Members',
      properties: 'Properties',
      teams: 'Teams',
    },
    card: {
      createdAt: 'Created {date}',
    },
    detail: {
      title: 'Workspace Details',
      loading: 'Loading workspace details...',
      loadError: 'Failed to load workspace details',
      sectionInformation: 'Workspace Information',
      sectionConfig: 'Configuration',
      sectionMembers: 'Recent Members ({count})',
      sectionProperties: 'Recent Properties ({count})',
      sectionInvitations: 'Pending Invitations ({count})',
      sectionUsage: 'Usage',
      sectionFinancials: 'Financials',
      sectionBrowse: 'Browse tenant data',
      sectionActivity: 'Recent activity',
      viewAll: 'View all',
      activityEmpty: 'No recent activity',
      activityError: 'Could not load recent activity',
      legalHold: {
        title: 'Messaging legal hold',
        label: 'Place legal hold',
        caption: 'Freezes all message deletion (retention TTL and tombstone cleanup) for this workspace. Staff-only: a workspace cannot lift a hold placed on itself.',
        updated: 'Legal hold updated.',
        updateFailed: 'Could not update the legal hold.',
      },
      usage: {
        storage: 'Storage',
      },
      financials: {
        totalRevenue: 'Total revenue',
        overdue: 'Overdue',
      },
      invitations: {
        inviteLink: 'Invite link',
        invitedBy: 'Invited by {name}',
        grantsAccess: 'Grants access to:',
        grantFallback: 'Resource',
      },
      emptyMembers: 'No members',
      emptyProperties: 'No properties',
      emptyInvitations: 'No pending invitations',
      description: {
        add: 'Add description',
        saved: 'Description saved',
        saveError: 'Failed to save the description',
        removed: 'Description removed',
        removeError: 'Failed to remove the description',
      },
      fields: {
        uuid: 'UUID',
        orgCode: 'Org code',
        billingEntity: 'Billing entity',
        description: 'Description',
        created: 'Created',
        lastUpdated: 'Last Updated',
        suspendedAt: 'Suspended',
        suspensionReason: 'Suspension reason',
      },
      actions: {
        edit: 'Edit',
        suspend: 'Suspend',
        reactivate: 'Reactivate',
        editConfig: 'Edit config',
      },
      config: {
        mapProvider: 'Map provider',
        diskLimit: 'Disk limit (MB)',
        messageLimit: 'Message limit',
        unlimited: 'Unlimited',
        megabytes: '{value} MB',
        geoTracking: 'Location tracking',
        geoDisabled: 'Disabled',
        geoStampOnly: 'Start/stop only',
        geoIntervalEvery: 'Continuous (every {minutes} min)',
        providers: {
          google: 'Google Maps',
          osm: 'OpenStreetMap',
        },
      },
      editDialog: {
        title: 'Edit Workspace',
        nameLabel: 'Name',
      },
      configDialog: {
        title: 'Edit Configuration',
        hint: 'Ad-trigger limits and map provider. A cleared limit means unlimited (no ads).',
        diskUnlimited: 'Unlimited disk',
        messageUnlimited: 'Unlimited messages',
        geoSection: 'Location tracking (staff-enabled)',
        geoStamp: 'Start/stop location stamp',
        geoInterval: 'Continuous interval tracking',
        geoBetriebsvereinbarung: 'Betriebsvereinbarung reference (DE)',
        geoWorksCouncil: 'Works-council consent date (NL)',
        geoPlNotice: 'Employee notice date (PL)',
      },
      suspendDialog: {
        title: 'Suspend Workspace',
        reasonLabel: 'Reason for suspension',
        submit: 'Suspend',
      },
      toasts: {
        updated: 'Workspace updated',
        updateFailed: 'Failed to update workspace',
        suspended: 'Workspace suspended',
        suspendFailed: 'Failed to suspend workspace',
        reactivated: 'Workspace reactivated',
        reactivateFailed: 'Failed to reactivate workspace',
        configUpdated: 'Configuration updated',
        configUpdateFailed: 'Failed to update configuration',
        geoPrerequisiteMissing: 'Cannot enable: the jurisdiction attestation (Betriebsvereinbarung / works-council / employee notice) is missing.',
        geoLeadTimeRequired: 'Cannot enable: the Polish employee notice must be given at least 14 days before activation.',
      },
    },
  },
  marketingTemplates: {
    subtitle: 'Preview marketing email templates rendered with sample data',
    filters: {
      brand: 'Brand',
      language: 'Language',
      type: 'Type',
    },
    empty: 'No templates found',
    listError: 'Could not load templates',
    noBrand: 'No brand',
    selectPrompt: 'Select a template to preview it',
    subjectLabel: 'Subject',
    previewFrameTitle: 'Email preview',
    viewport: {
      desktop: 'Desktop',
      mobile: 'Mobile',
    },
    part: {
      html: 'HTML',
      text: 'Plain text',
    },
    hygiene: {
      unsubscribe: 'Unsubscribe link',
      address: 'Postal address',
      imageBalance: 'Image and text balance',
      links: 'Links',
    },
    previewError: 'The preview could not be rendered',
    types: {
      promotion: 'Promotion',
      newsletter: 'Newsletter',
      offer: 'Offer',
      lifecycle: 'Lifecycle',
    },
  },
  acquisition: {
    landingsEmpty: 'No landing events yet',
    attributionsEmpty: 'No attributions yet',
    subtitle: 'Track inbound traffic, spend, and conversion by channel.',
    loadFailed: 'Failed to load acquisition data',
    filterBySource: 'Filter by source',
    filterByCampaign: 'Filter by campaign',
    from: 'From',
    to: 'To',
    export: 'Export CSV',
    tabs: {
      overview: 'Overview',
      report: 'Report',
      events: 'Events',
      spend: 'Spend',
      email: 'Email',
      landings: 'Landings',
      attributions: 'Attributions',
      funnel: 'Funnel',
    },
    presets: {
      d7: '7 days',
      d30: '30 days',
      d90: '90 days',
    },
    kpi: {
      spend: 'Spend',
      signups: 'Signups',
      cac: 'CAC',
      revenue: 'Revenue',
      roas: 'ROAS',
    },
    overview: {
      bestChannel: 'Best channel (ROAS)',
      worstChannel: 'Costliest channel (CAC)',
      unattributed: 'Unattributed signups',
      unattributedHint: 'Signups with no tracked source',
      channelShare: 'Channel share (by landings)',
      noChannels: 'No channel data for this period',
    },
    trend: {
      signupsAria: 'Daily signups over the selected period',
      spendAria: 'Daily ad spend over the selected period',
    },
    groupBy: {
      source: 'By source',
      channel: 'By channel',
      campaign: 'By campaign',
    },
    reportExtras: {
      sourceScope: 'Campaigns for {source}',
      clearScope: 'All sources',
      drilldown: 'View campaigns',
      unclassifiedTitle: 'Unclassified sources',
      unclassifiedHint: 'Traffic bucketed as referral or other. If these are known platforms, the channel classifier needs extending.',
      clickIdsTitle: 'Click-ID presence',
      clickIdsHint: 'Landings carrying ad click IDs per source (counts only, values stay private).',
    },
    budgets: {
      title: 'Monthly budgets',
      addTitle: 'Set a monthly budget',
      dimension: 'Dimension',
      bySource: 'Source',
      byChannel: 'Channel group',
      month: 'Month',
      saved: 'Budget saved',
      saveFailed: 'Failed to save budget',
      deleted: 'Budget deleted',
      confirmDelete: {
        title: 'Delete this budget?',
        message: 'The budget row and its figures will be removed from reporting. This cannot be undone.',
      },
      deleteFailed: 'Failed to delete budget',
      editTitle: 'Edit budget',
      updated: 'Budget updated',
      updateFailed: 'Failed to update budget',
      budget: 'Budget',
      spent: 'Spent to date',
      expected: 'Expected to date',
      variance: 'Variance',
      overPace: 'Over pace',
    },
    email: {
      title: 'Email campaigns',
      unavailable: 'Campaign metrics are unavailable right now. The marketing service could not be reached.',
      name: 'Name',
      brand: 'Brand',
      status: 'Status',
      sentAt: 'Sent at',
      sent: 'Sent',
      delivered: 'Delivered',
      opened: 'Opened',
      clicked: 'Clicked',
      openRate: 'Open rate',
      clickRate: 'Click rate',
    },
    channels: {
      paidSearch: 'Paid Search',
      paidSocial: 'Paid Social',
      organicSearch: 'Organic Search',
      organicSocial: 'Organic Social',
      display: 'Display',
      email: 'Email',
      referral: 'Referral',
      affiliate: 'Affiliate',
      direct: 'Direct',
      other: 'Other',
    },
    cols: {
      source: 'Source',
      channel: 'Channel',
      medium: 'Medium',
      campaign: 'Campaign',
      landings: 'Landings',
      signups: 'Signups',
      revenue: 'Revenue',
      spend: 'Spend',
      cpl: 'CPL',
      cac: 'CAC',
      roas: 'ROAS',
      conversion: 'Conversion',
      referrer: 'Referrer',
      landingPath: 'Landing page',
      landedAt: 'Landed at',
      user: 'User',
      email: 'Email',
      firstLanded: 'First landed',
      signedUp: 'Signed up',
    },
    spend: {
      addTitle: 'Record channel spend',
      date: 'Date',
      amount: 'Amount',
      currency: 'Currency',
      note: 'Note',
      save: 'Save',
      saved: 'Spend saved',
      saveFailed: 'Failed to save spend',
      deleted: 'Spend deleted',
      confirmDelete: {
        title: 'Delete this spend entry?',
        message: 'The spend entry and its figures will be removed from reporting. This cannot be undone.',
      },
      deleteFailed: 'Failed to delete spend',
      editTitle: 'Edit spend entry',
      updated: 'Spend updated',
      updateFailed: 'Failed to update spend',
    },
    funnel: {
      overallConversion: 'Overall conversion',
      fromPrevious: 'from previous',
      empty: 'No funnel data for this period',
      steps: {
        sessions: 'Sessions',
        registrations: 'Registrations',
        emailVerified: 'Email verified',
        onboardingCompleted: 'Onboarding completed',
        workspaceCreated: 'Workspace created',
        subscriptionStarted: 'Subscription started',
        upgradeCompleted: 'Upgrade completed',
      },
    },
  },
  ads: {
    title: 'Advertisements',
    newAd: 'New Ad',
    searchPlaceholder: 'Search ads...',
    loadingAds: 'Loading advertisements...',
    loadFailed: 'Failed to load advertisements',
    stats: {
      total: 'Total',
    },
    tabs: {
      all: 'All',
      draft: 'Draft',
      pending: 'Pending',
      active: 'Active',
      paused: 'Paused',
      rejected: 'Rejected',
    },
    actions: {
      submit: 'Submit',
      approve: 'Approve',
      reject: 'Reject',
      pause: 'Pause',
      resume: 'Resume',
      submitForReview: 'Submit for Review',
      createAd: 'Create Ad',
    },
    detail: {
      title: 'Ad Details',
      fields: {
        title: 'Title',
        description: 'Description',
        targetUrl: 'Target URL',
        status: 'Status',
        placement: 'Placement',
        type: 'Type',
        rejectionReason: 'Rejection Reason',
        impressions: 'Impressions',
        clicks: 'Clicks',
        ctr: 'CTR',
        budget: 'Budget',
        starts: 'Starts',
        ends: 'Ends',
        createdBy: 'Created By',
        approved: 'Approved',
        created: 'Created',
      },
    },
    form: {
      createTitle: 'Create Advertisement',
      editTitle: 'Edit Advertisement',
      workspace: 'Workspace *',
      workspaceRequired: 'Workspace is required',
      noWorkspace: 'No workspaces found',
      title: 'Title *',
      titleRequired: 'Title is required',
      description: 'Description',
      targetUrl: 'Target URL *',
      targetUrlRequired: 'Target URL is required',
      imageUrl: 'Image URL',
      altText: 'Alt Text',
      placement: 'Placement *',
      placementRequired: 'Placement is required',
      type: 'Type *',
      typeRequired: 'Type is required',
      advertiser: 'Advertiser',
      advertiserRequired: 'Advertiser is required for marketplace ads',
      leadPriceCents: 'Lead price (cents)',
      leadPriceHint: 'Charged to the advertiser per delivered lead, in euro cents.',
      leadPriceRequired: 'Lead price is required for marketplace ads',
      startDate: 'Start Date',
      endDate: 'End Date',
      budget: 'Budget',
      budgetType: 'Budget Type',
    },
    placements: {
      sidebar: 'Sidebar',
      banner: 'Banner',
      inline: 'Inline',
      popup: 'Popup',
    },
    types: {
      internal: 'Internal (EMALINO promo)',
      sponsored: 'Sponsored',
      partner: 'Partner',
      marketplace: 'Marketplace',
    },
    budgetTypes: {
      daily: 'Daily',
      total: 'Total',
    },
    rejectDialog: {
      title: 'Reject Advertisement',
      reason: 'Reason for rejection',
    },
    deleteDialog: {
      title: 'Delete Advertisement',
      message: 'Are you sure you want to delete this advertisement? This cannot be undone.',
    },
    notifications: {
      created: 'Advertisement created',
      updated: 'Advertisement updated',
      submitted: 'Ad submitted for review',
      approved: 'Ad approved',
      rejected: 'Ad rejected',
      paused: 'Ad paused',
      resumed: 'Ad resumed',
      deleted: 'Ad deleted',
      createFailed: 'Failed to create advertisement',
      updateFailed: 'Failed to update advertisement',
      submitFailed: 'Failed to submit ad',
      approveFailed: 'Failed to approve ad',
      rejectFailed: 'Failed to reject ad',
      pauseFailed: 'Failed to pause ad',
      resumeFailed: 'Failed to resume ad',
      deleteFailed: 'Failed to delete ad',
    },
  },
  advertisers: {
    newAdvertiser: 'New Advertiser',
    loadFailed: 'Failed to load advertisers',
    filters: {
      search: 'Search by name or email',
      status: 'Status',
      statusAll: 'All statuses',
    },
    columns: {
      name: 'Name',
      contactEmail: 'Contact Email',
      status: 'Status',
      balance: 'Balance',
    },
    actions: {
      topUp: 'Top up balance',
      createAdvertiser: 'Create Advertiser',
    },
    form: {
      createTitle: 'Create Advertiser',
      editTitle: 'Edit Advertiser',
      name: 'Name',
      nameRequired: 'Name is required',
      contactEmail: 'Contact Email',
      leadWebhookUrl: 'Lead Webhook URL',
      initialBalance: 'Initial balance',
    },
    topUpDialog: {
      title: 'Top Up Balance',
      amount: 'Amount',
      reason: 'Reason (optional, audited)',
    },
    deleteDialog: {
      title: 'Delete Advertiser',
      message: 'Are you sure you want to delete {name}? This cannot be undone.',
    },
    notifications: {
      created: 'Advertiser created',
      createFailed: 'Failed to create advertiser',
      updated: 'Advertiser updated',
      updateFailed: 'Failed to update advertiser',
      toppedUp: 'Balance topped up',
      topUpFailed: 'Failed to top up balance',
      deleted: 'Advertiser deleted',
      deleteFailed: 'Failed to delete advertiser',
    },
  },
  marketplaceLeads: {
    loadFailed: 'Failed to load marketplace leads',
    filters: {
      status: 'Status',
      statusAll: 'All statuses',
      search: 'Search by contact name or email',
    },
    columns: {
      workspace: 'Workspace',
      contact: 'Contact',
      price: 'Price',
      status: 'Status',
      createdAt: 'Captured',
      actions: 'Actions',
    },
    actions: {
      accept: 'Accept',
      reject: 'Reject',
      refund: 'Refund',
    },
    reviewDialog: {
      title: {
        accept: 'Accept Lead',
        reject: 'Reject Lead',
        refund: 'Refund Lead',
      },
      reason: 'Reason (optional, audited)',
      refundNotice: 'This credits {amount} back to the advertiser balance.',
    },
    notifications: {
      acceptSuccess: 'Lead accepted',
      acceptFailed: 'Failed to accept lead',
      rejectSuccess: 'Lead rejected',
      rejectFailed: 'Failed to reject lead',
      refundSuccess: 'Lead refunded and advertiser balance credited',
      refundFailed: 'Failed to refund lead',
    },
  },
  audit: {
    searchPlaceholder: 'Search by description, user, or UUID...',
    loadFailed: 'Failed to load audit logs',
    filters: {
      action: 'Action',
      resourceType: 'Resource type',
      fromDate: 'From Date',
      toDate: 'To Date',
    },
    columns: {
      timestamp: 'Timestamp',
      user: 'User',
      action: 'Action',
      resource: 'Resource',
    },
    detail: {
      showDetails: 'Show details',
      hideDetails: 'Hide details',
      description: 'Description',
      oldValues: 'Old Values',
      newValues: 'New Values',
      metadata: 'Metadata',
      ipAddress: 'IP Address',
      userAgent: 'User Agent',
    },
    filterByActor: 'Filter the log by this user',
    showingActionsBy: 'Showing actions by {name}',
  },
  exports: {
    title: 'Exports',
    loadFailed: 'Failed to load exports',
    empty: 'No exports found',
    download: 'Download',
    downloadFailed: 'Download not available',
    generate: {
      label: 'Generate export',
      workspaces: 'Workspaces (CSV)',
      financial: 'Financial (CSV)',
      system: 'System data (JSON)',
      success: 'Export downloaded',
      failed: 'Export failed',
    },
    filters: {
      status: 'Status',
      type: 'Type',
      all: 'All',
    },
    columns: {
      type: 'Type',
      status: 'Status',
      size: 'Size',
      created: 'Created',
      expires: 'Expires',
      actions: 'Actions',
    },
    statuses: {
      pending: 'Pending',
      processing: 'Processing',
      completed: 'Completed',
      failed: 'Failed',
    },
    types: {
      user_data: 'Personal data (GDPR)',
      time_entries: 'Time entries',
      users: 'Users',
      workspaces: 'Workspaces',
      financial: 'Financial',
      tickets: 'Tickets',
      audit_log: 'Audit log',
      system_data: 'System data',
    },
  },
  clientLogs: {
    searchPlaceholder: 'Search by message...',
    export: 'Export NDJSON',
    loadFailed: 'Failed to load client logs',
    empty: 'No client logs found',
    filters: {
      userUuid: 'User UUID',
      level: 'Level',
      sessionId: 'Session ID',
      release: 'Release',
      fromDate: 'From Date',
      toDate: 'To Date',
    },
    columns: {
      timestamp: 'Timestamp',
      level: 'Level',
      logger: 'Logger',
      message: 'Message',
      userUuid: 'User UUID',
    },
    detail: {
      title: 'Client Log Details',
      message: 'Message',
      session: 'Session ID',
      route: 'Route',
      release: 'Release',
      platform: 'Platform',
      appVersion: 'App Version',
      deviceModel: 'Device Model',
      receivedAt: 'Received',
      correlationId: 'Correlation ID',
      context: 'Context',
      attributes: 'Attributes',
    },
  },
  staffMessageReports: {
    loadFailed: 'Failed to load message reports',
    empty: 'No reports in the queue',
    emptyFiltered: 'No reports match your filters',
    detailLabel: 'Reporter note',
    contentWithheld: 'Content withheld (encrypted conversation)',
    alreadyRedacted: 'Message already removed',
    dismissSuccess: 'Report dismissed',
    redactSuccess: 'Message redacted',
    actionFailed: 'Action failed',
    filters: {
      status: 'Status',
      reason: 'Reason',
      workspace: 'Workspace UUID',
    },
    statuses: {
      open: 'Open',
      dismissed: 'Dismissed',
      redacted: 'Redacted',
      all: 'All',
    },
    reasons: {
      spam: 'Spam',
      harassment: 'Harassment',
      inappropriate: 'Inappropriate',
      other: 'Other',
    },
    columns: {
      workspace: 'Workspace',
      reporter: 'Reporter',
      reason: 'Reason',
      message: 'Message',
      reportedAt: 'Reported',
      status: 'Status',
      actions: 'Actions',
    },
    actions: {
      dismiss: 'Dismiss',
      redact: 'Redact',
    },
    confirmDismiss: {
      title: 'Dismiss report?',
      message: 'Mark this report as reviewed with no action. The message stays visible.',
    },
    confirmRedact: {
      title: 'Redact message?',
      message: 'Hide this message from all participants (it becomes a deleted placeholder) and resolve the report. This cannot be undone.',
    },
  },
  staffConversations: {
    searchPlaceholder: 'Search conversations...',
    loadFailed: 'Failed to load conversations',
    empty: 'No conversations found',
    emptyFiltered: 'No conversations found matching your criteria',
    exportLabel: 'Export thread',
    exportSuccess: 'Conversation export started',
    exportFailed: 'Failed to export conversation',
    encryptedBadge: 'Encrypted',
    filters: {
      workspace: 'Workspace UUID',
      type: 'Type',
      encrypted: 'Encrypted',
      fromDate: 'From Date',
      toDate: 'To Date',
      includeDeleted: 'Include deleted',
      encryptedYes: 'Encrypted',
      encryptedNo: 'Not encrypted',
    },
    types: {
      direct: 'Direct',
      group: 'Group',
      resource: 'Resource',
    },
    systemMessageTypes: {
      user_added: 'User added',
      user_removed: 'User removed',
      conversation_renamed: 'Conversation renamed',
      role_changed: 'Role changed',
      user_left: 'User left',
      conversation_created: 'Conversation created',
    },
    columns: {
      workspace: 'Workspace',
      name: 'Name',
      type: 'Type',
      participants: 'Participants',
      messages: 'Messages',
      lastActivity: 'Last Activity',
      encrypted: 'Encrypted',
      actions: 'Actions',
    },
    detail: {
      backToList: 'Back to conversations',
      info: 'Conversation Info',
      workspace: 'Workspace',
      type: 'Type',
      createdBy: 'Created by',
      createdAt: 'Created at',
      participantCountLabel: 'Participants',
      messageCountLabel: 'Messages',
      participants: 'Participants',
      role: 'Role',
      left: 'Left',
      active: 'Active',
      thread: 'Message Thread',
      noMessages: 'No messages in this conversation',
      loadMore: 'Load more',
      encryptedNotice: 'Encrypted, content not available',
      deletedBadge: 'Deleted',
      editedBadge: 'Edited',
      systemMessage: 'System message',
      notFound: 'Conversation not found',
      deletedNotice: 'This conversation has been deleted.',
      deletedAt: 'Deleted at',
    },
  },
  clientLogPolicies: {
    subtitle: 'Remote debug-logging policies across all users',
    loadFailed: 'Failed to load logging policies',
    empty: 'No logging policies found',
    emptyFiltered: 'No logging policies match your criteria',
    legalHold: 'Legal hold',
    filters: {
      userUuid: 'User UUID',
      status: 'Status',
      statusAll: 'All',
      statusActive: 'Active',
      statusInactive: 'Inactive',
    },
    status: {
      active: 'Active',
      expired: 'Expired',
    },
    columns: {
      userUuid: 'User UUID',
      status: 'Status',
      level: 'Level',
      sampleRate: 'Sample rate',
      expires: 'Expires',
      reason: 'Reason',
    },
    actions: {
      edit: 'Edit policy',
      disable: 'Disable',
    },
    fields: {
      level: 'Log level',
      sampleRatePercent: 'Sample rate (%)',
      retentionDays: 'Retention (days)',
      extendHours: 'Extend by (hours)',
      extendHoursHint: 'Leave 0 to keep the current expiry',
      reason: 'Reason',
    },
    editDialog: {
      title: 'Edit logging policy',
    },
    disableDialog: {
      title: 'Disable logging policy',
      reason: 'Reason for disabling',
    },
    toasts: {
      updated: 'Policy updated',
      updateFailed: 'Failed to update policy',
      disabled: 'Policy disabled',
      disableFailed: 'Failed to disable policy',
    },
  },
  settings: {
    title: 'System Settings',
    subtitle: 'Configure system-wide settings and preferences',
    loadingSettings: 'Loading settings...',
    loadFailed: 'Failed to load settings',
    saveFailed: 'Failed to save settings',
    saveSuccess: '{group} settings saved',
    saveChanges: 'Save Changes',
    groupSettings: '{group} Settings',
  },
  moderation: {
    title: 'Content Moderation',
    subtitle: 'Review and moderate uploaded content',
    loadingQueue: 'Loading moderation queue...',
    loadFailed: 'Failed to load queue',
    stats: {
      pending: 'Pending',
      flagged: 'Flagged',
      approvedToday: 'Approved Today',
      rejectedToday: 'Rejected Today',
    },
    tabs: {
      all: 'All Pending',
      pending: 'Pending',
      flagged: 'Flagged',
    },
    captureFilter: {
      all: 'All',
      captured: 'Captured in app',
      uploaded: 'Uploaded',
    },
    typeFilter: {
      label: 'Media type',
      all: 'All types',
      image: 'Images',
      video: 'Videos',
      text: 'Documents',
    },
    aiScore: 'AI Score: {score}',
    risk: { high: 'High risk', medium: 'Medium risk', low: 'Low risk' },
    detail: {
      title: 'File Details',
      preview: {
        open: 'Open original',
        unavailable: 'Preview unavailable',
      },
      fields: {
        fileName: 'File Name',
        type: 'Type',
        size: 'Size',
        source: 'Source',
        captureMethod: 'Capture method',
        status: 'Status',
        aiScore: 'AI Score',
        aiFlags: 'AI Flags',
        notes: 'Notes',
        uploaded: 'Uploaded',
      },
    },
    rejectDialog: {
      title: 'Reject Content',
      reason: 'Reason for rejection',
    },
    notifications: {
      approved: 'File approved',
      rejected: 'File rejected',
      approveFailed: 'Failed to approve file',
      rejectFailed: 'Failed to reject file',
    },
  },
  activity: {
    searchPlaceholder: 'Search actor, action, target or summary',
    loadFailed: 'Failed to load activity',
    filters: {
      domain: 'Domain',
      actor: 'Actor',
      fromDate: 'From date',
      toDate: 'To date',
    },
    columns: {
      when: 'When',
      domain: 'Domain',
      actor: 'Actor',
      action: 'Action',
      target: 'Target',
    },
    detail: {
      hide: 'Hide details',
      show: 'Show details',
      summary: 'Summary',
      ipAddress: 'IP address',
      correlationId: 'Correlation ID',
    },
    domains: {
      resource: 'Resource',
      auth: 'Authentication',
      acl: 'Access control',
      credential: 'Credentials',
      membership: 'Membership',
      subscription: 'Subscription',
      staff: 'Staff',
    },
  },
  sentEmails: {
    searchPlaceholder: 'Search by recipient or subject',
    loadFailed: 'Failed to load sent emails',
    resendSuccess: 'Email queued for resend',
    resendFailed: 'Failed to resend email',
    filters: {
      type: 'Email type',
      status: 'Status',
      fromDate: 'From date',
      toDate: 'To date',
    },
    columns: {
      sentAt: 'Sent at',
      recipient: 'Recipient',
      type: 'Type',
      status: 'Status',
      attempts: 'Attempts',
    },
    actions: {
      resend: 'Resend',
    },
    detail: {
      hide: 'Hide details',
      show: 'Show details',
      subject: 'Subject',
      messageId: 'Provider message ID',
      lastStatusAt: 'Last status at',
      lastError: 'Last error',
    },
    resendConfirm: {
      title: 'Resend email',
      message: 'Resend this email to {email}?',
    },
  },
  // Shared status labels used by StatusChip across tickets / users / workspaces
  statuses: {
    ai_active: 'AI chat',
    ai_waiting: 'AI replying',
    agent_active: 'Agent active',
    abandoned: 'Abandoned',
    candidate: 'Candidate',
    promoted: 'Promoted',
    deprecated: 'Deprecated',
    accept: 'Accepted',
    reject: 'Rejected',
    relabel: 'Relabelled',
    new: 'New',
    assigned: 'Assigned',
    open: 'Open',
    in_progress: 'In Progress',
    waiting: 'Waiting',
    waiting_user: 'Waiting for user',
    waiting_staff: 'Waiting for staff',
    waiting_for_agent: 'Waiting for agent',
    resolved: 'Resolved',
    closed: 'Closed',
    pending: 'Pending',
    pending_verification: 'Pending Verification',
    under_review: 'Under Review',
    approved: 'Approved',
    rejected: 'Rejected',
    cancelled: 'Cancelled',
    completed: 'Completed',
    active: 'Active',
    suspended: 'Suspended',
    enabled: 'Enabled',
    disabled: 'Disabled',
    low: 'Low',
    normal: 'Normal',
    medium: 'Medium',
    high: 'High',
    urgent: 'Urgent',
    queued: 'Queued',
    sent: 'Sent',
    delivered: 'Delivered',
    bounced: 'Bounced',
    complained: 'Complained',
    failed: 'Failed',
    debug: 'Debug',
    info: 'Info',
    warning: 'Warning',
    error: 'Error',
    fatal: 'Fatal',
    accepted: 'Accepted',
    refunded: 'Refunded',
  },
  sessions: {
    title: 'Active sessions',
    subtitle: 'Devices currently signed in to your staff account.',
    loadFailed: 'Could not load your sessions.',
    unknownDevice: 'Unknown device',
    current: 'This device',
    lastActive: 'Last active',
    revoke: 'Sign out',
    revoked: 'Session signed out.',
    revokeFailed: 'Could not sign out that session.',
    revokeConfirm: {
      title: 'Sign out this session?',
      message: 'That device will be signed out of your staff account.',
    },
    logoutAll: 'Sign out of all devices',
    logoutAllConfirm: {
      title: 'Sign out everywhere?',
      message: 'This signs you out of every device, including this one. You will need to sign in again.',
    },
    logoutAllSuccess: 'Signed out of all devices.',
    logoutAllFailed: 'Could not sign out of all devices.',
  },
  commandPalette: {
    placeholder: 'Search or type a command...',
    openLabel: 'Open command palette',
    ariaLabel: 'Command palette',
    noResults: 'No results for this query',
    sections: {
      navigation: 'Navigate',
      actions: 'Actions',
    },
    actions: {
      toggleTheme: 'Toggle dark mode',
      notificationSettings: 'Notification settings',
    },
  },
  filters: {
    active: 'Active filters',
    clearAll: 'Clear all',
  },
  emptyState: {
    filtered: {
      title: 'Nothing matches those filters',
      body: 'Rows may still exist here; the current filters are hiding them. Adjust or clear the filters to see everything.',
      queryTitle: 'No results for "{query}"',
      queryBody: 'Check the spelling, or try a shorter or more general term.',
      action: 'Clear filters',
    },
    knowledge: {
      title: 'No knowledge articles yet',
      body: 'Auto-built and manually authored articles appear here once created.',
    },
    reviewQueue: {
      title: 'Review queue is empty',
      body: 'Auto-generated drafts flagged for review appear here.',
    },
    tickets: {
      title: 'No tickets found',
      body: 'Support tickets will appear here once users submit requests.',
    },
    users: {
      title: 'No users found',
      body: 'Registered users will appear here.',
    },
    workspaces: {
      title: 'No workspaces found',
      body: 'Workspaces will appear here once users create their organisations.',
    },
    audit: {
      title: 'No audit logs found',
      body: 'System activity will be recorded here.',
    },
    moderation: {
      title: 'Moderation queue is empty',
      body: 'Files requiring review will appear here.',
    },
    chat: {
      title: 'No chat sessions',
      body: 'Active support chat sessions will appear here.',
    },
    ads: {
      title: 'No advertisements',
      body: 'Create your first ad campaign to get started.',
    },
    deletions: {
      title: 'No deletion requests',
      body: 'Workspace deletion requests will appear here.',
    },
    abTests: {
      title: 'No A/B tests found',
      body: 'Enable A/B testing on feature flags to see results here.',
    },
    email: {
      title: 'No sent emails found',
      body: 'Outbound transactional emails will appear here.',
    },
    advertisers: {
      title: 'No advertisers found',
      body: 'Create your first advertiser account to get started.',
    },
    marketplaceLeads: {
      title: 'No marketplace leads found',
      body: 'Captured construction-marketplace leads will appear here for review.',
    },
  },
  bulk: {
    toolbar: 'Bulk actions',
    selected: '{count} selected',
    clearSelection: 'Clear selection',
    close: 'Close',
    escalate: 'Escalate',
    approve: 'Approve',
    reject: 'Reject',
    pause: 'Pause',
    resume: 'Resume',
    suspend: 'Suspend',
    unsuspend: 'Unsuspend',
    delete: 'Delete',
    success: 'Processed {processed} items ({failed} failed)',
    confirmTitle: 'Confirm bulk action',
    confirmMessage: 'Apply "{action}" to {count} selected items?',
  },
  export: {
    csv: 'Export CSV',
    success: 'Export downloaded successfully',
    failed: 'Export failed',
  },
  deletionRequests: {
    list: {
      search: 'Search by workspace name...',
      searchPlaceholder: 'Search by workspace name...',
      filterStatus: 'Filter by status',
      stats: {
        pending: 'Pending',
        underReview: 'Under review',
        suspended: 'Suspended',
      },
      allStatuses: 'All Statuses',
      viewDetails: 'View Details',
      columns: {
        workspace: 'Workspace',
        status: 'Status',
        requestedBy: 'Requested By',
        requestedDate: 'Requested Date',
        actions: 'Actions',
      },
    },
    detail: {
      title: 'Deletion Request Details',
      notFound: 'Deletion request not found',
      backToList: 'Back to List',
      sections: {
        workspaceInfo: 'Workspace Information',
        requestDetails: 'Request Details',
        currentBlockers: 'Current Blockers',
        timeline: 'Timeline',
        actions: 'Actions',
        reviewNotes: 'Review Notes',
      },
      fields: {
        name: 'Name',
        code: 'Code',
        status: 'Status',
        requestedBy: 'Requested By',
        requestedDate: 'Requested Date',
        ipAddress: 'IP Address',
        reasonForDeletion: 'Reason for Deletion',
      },
      timeline: {
        created: 'Request Created',
        createdDescription: '{name} submitted deletion request',
        underReview: 'Under Review',
        underReviewDescription: '{name} started review',
        approved: 'Approved',
        approvedDescription: '{name} approved request',
        approvedNotes: 'Notes: {notes}',
        rejected: 'Rejected',
        rejectedDescription: '{name} rejected request',
        rejectedReasonLabel: 'Reason: {reason}',
        scheduled: 'Scheduled for Deletion',
        scheduledDescription: 'Workspace will be permanently deleted',
        daysRemaining: '{count} days remaining',
      },
      buttons: {
        markUnderReview: 'Mark Under Review',
        approveRequest: 'Approve Request',
        rejectRequest: 'Reject Request',
        executeDeletion: 'Execute Deletion',
        unsuspendWorkspace: 'Unsuspend Workspace',
        saveReviewNotes: 'Save Notes',
      },
      reviewNotesPlaceholder: 'Internal notes about this request (staff-only)',
      actions: {
        approveTitle: 'Approve Deletion Request',
        approveMessage: 'This will suspend the workspace for 30 days before permanent deletion.',
        approveNotes: 'Notes (optional)',
        rejectTitle: 'Reject Deletion Request',
        rejectMessage: 'Please provide a reason for rejection (minimum 20 characters):',
        rejectReason: 'Reason',
        rejectValidation: 'Reason must be at least 20 characters',
        executeTitle: 'Execute Permanent Deletion',
        executeMessage: 'WARNING: This will permanently delete the workspace and all its data. This action cannot be undone.',
        unsuspendTitle: 'Unsuspend Workspace',
        unsuspendMessage: 'Please provide a reason for unsuspending (minimum 20 characters):',
        unsuspendReason: 'Reason',
        unsuspendValidation: 'Reason must be at least 20 characters',
        markedUnderReview: 'Marked as under review',
        notesSaved: 'Review notes saved',
        notesSaveFailed: 'Failed to save review notes',
        approvedSuccess: 'Request approved successfully',
        approvedError: 'Failed to approve request',
        rejectedSuccess: 'Request rejected successfully',
        rejectedError: 'Failed to reject request',
        executedSuccess: 'Workspace deleted permanently',
        executedError: 'Failed to execute deletion',
        unsuspendedSuccess: 'Workspace unsuspended successfully',
        unsuspendedError: 'Failed to unsuspend workspace',
      },
    },
    status: {
      pending: 'Pending',
      underReview: 'Under Review',
      approved: 'Approved',
      rejected: 'Rejected',
      suspended: 'Suspended',
      cancelled: 'Cancelled',
      completed: 'Completed',
    },
  },
  abTests: {
    page: {
      subtitle: 'Statistical analysis and winner determination for A/B tested feature flags',
      refresh: 'Refresh',
      backToFlags: 'Back to Flags',
    },
    empty: {
      messagePrefix: 'Enable A/B testing on feature flags by setting',
      enabledKeyHint: 'ab_test_enabled: true',
      messageSuffix: 'in the config',
    },
    variants: {
      controlTitle: 'Control (Baseline)',
      treatmentTitle: 'Treatment (New Version)',
      exposures: 'Exposures',
      uniqueUsers: 'Unique Users',
    },
    confidence: {
      veryHigh: 'Very High (99%+)',
      high: 'High (95%+)',
      low: 'Low (<95%)',
      unknown: 'Unknown',
    },
    recommendation: {
      enableForAll: 'Enable for All Users',
      keepDisabled: 'Keep Disabled',
      continueTest: 'Continue Testing',
      unknown: 'Unknown',
    },
    winner: {
      none: 'No Winner',
      treatment: 'Treatment Wins',
      control: 'Control Wins',
    },
    conversion: {
      sectionTitle: 'Conversion Events',
      rateLine: 'Control: {control}% | Treatment: {treatment}%',
      empty: 'No conversion events tracked yet',
      viewAnalysisTooltip: 'View Statistical Analysis',
    },
    actions: {
      viewResults: 'View Full Results',
      resetData: 'Reset Data',
      viewResultsToast: 'Full results view for {flag} - Coming soon',
      resetSuccess: 'A/B test data reset successfully',
      resetFailed: 'Failed to reset A/B test data',
      confirmResetTitle: 'Reset A/B Test Data',
      confirmResetMessage: 'Are you sure you want to reset all data for "{name}"? This action cannot be undone.',
    },
    analysis: {
      title: 'Statistical Analysis',
      flag: 'Flag',
      conversionEvent: 'Conversion Event',
      significance: 'Statistical Significance',
      pValue: 'p-value: {value}',
      controlRate: 'Control Rate',
      treatmentRate: 'Treatment Rate',
      relativeImprovement: 'Relative Improvement',
      sampleSizes: 'Sample Sizes',
      sampleSizesLine: 'Control: {control} | Treatment: {treatment}',
      winnerDetermination: 'Winner Determination',
    },
  },
  featureFlags: {
    page: {
      subtitle: 'Read-only dashboard for feature flags configuration and lifecycle management',
      refresh: 'Refresh',
      viewMetrics: 'View Metrics',
      abTests: 'A/B Tests',
      validateConfig: 'Validate Config',
      empty: 'No feature flags configured',
      emptyBody: 'Flags defined in the application configuration appear here once they are in sync.',
    },
    validation: {
      failed: 'Configuration validation failed',
      hasWarnings: 'Configuration has warnings',
      valid: 'Configuration is valid ({count} flags in sync)',
    },
    stats: {
      totalFlags: 'Total Flags',
      enabled: 'Enabled',
      disabledCaption: '{count} disabled',
      expiringThisWeek: 'Expiring This Week',
      expiringThisMonthCaption: '{count} expiring this month',
      fullyRolledOut: 'Fully Rolled Out',
      partialCaption: '{count} partial',
    },
    filters: {
      typeLabel: 'Type',
      enabledLabel: 'Enabled',
      quickAll: 'All',
      quickExpiring: 'Expiring',
    },
    badge: {
      expired: 'Expired',
      soon: 'Soon',
      permanent: 'Permanent',
      enabled: 'Enabled',
      disabled: 'Disabled',
    },
    columns: {
      key: 'Flag Key',
      enabled: 'Enabled',
      type: 'Type',
      rollout: 'Rollout',
      expiry: 'Expiry',
      allowedRoles: 'Allowed Roles',
      actions: 'Actions',
    },
    actions: {
      viewDetails: 'View Details',
    },
    metrics: {
      empty: 'No evaluations recorded yet',
      emptyBody: 'Counts appear here once the application starts checking flags.',
      page: {
        subtitle: 'Real-time observability dashboard for feature flag evaluations',
        refresh: 'Refresh',
        backToFlags: 'Back to Flags',
        lastUpdated: 'Last updated: {timestamp}',
      },
      summary: {
        totalEvaluations: 'Total Evaluations',
        totalEvaluationsCaption: 'Across all flags',
        uniqueUsers: 'Unique Users',
        uniqueUsersCaption: 'Distinct users tracked',
        uniqueWorkspaces: 'Unique Workspaces',
        uniqueWorkspacesCaption: 'Distinct workspaces tracked',
        totalFlags: 'Total Flags',
        totalFlagsCaption: 'Feature flags tracked',
      },
      topEvaluated: {
        title: 'Top Evaluated Flags',
        evalsLine: '{count} evals',
        enabledDisabledLine: '{enabled} enabled / {disabled} disabled',
        usersLine: '{count} users',
        empty: 'No evaluation data available yet',
      },
      mostUsers: {
        title: 'Most Popular Flags (by Users)',
        usersLine: '{count} users',
        workspacesLine: '{count} workspaces',
        evalsLine: '{count} evals',
        empty: 'No user data available yet',
      },
      table: {
        title: 'All Flags Metrics',
        columns: {
          flag: 'Flag',
          totalEvals: 'Total Evals',
          enabledDisabled: 'Enabled/Disabled',
          uniqueUsers: 'Unique Users',
          uniqueWorkspaces: 'Unique Workspaces',
          actions: 'Actions',
        },
        viewDetails: 'View Details',
      },
      dialog: {
        title: 'Metrics: {flag}',
        cardTotal: 'Total',
        cardEnabled: 'Enabled',
        cardDisabled: 'Disabled',
        cardUsers: 'Users',
        dailyTitle: 'Daily Evaluations (Last 30 Days)',
        dailyTooltip: '{date}: {count} evals',
        ratioTitle: 'Enabled vs Disabled Ratio',
        ratioEnabledPercent: '{percent}% enabled',
        close: 'Close',
      },
    },
    detail: {
      key: 'Key',
      description: 'Description',
      enabled: 'Enabled',
      type: 'Type',
      rolloutSection: 'Status & Rollout',
      rolloutPercentage: 'Rollout Percentage',
      expiryDate: 'Expiry Date',
      access: 'Access',
      allowedRoles: 'Allowed Roles',
      allowedWorkspaces: 'Allowed Workspaces',
      noneAll: 'All',
      conditions: 'Conditions',
      minVersion: 'Min Version',
      maxVersion: 'Max Version',
      environments: 'Environments',
      usageStats: 'Usage Stats',
      totalChecks: 'Total Checks',
      uniqueUsers: 'Unique Users',
      lastCheckedAt: 'Last Checked',
      never: 'Never',
      close: 'Close',
    },
  },
  maintenance: {
    empty: 'No maintenance rules yet',
    emptyBody: 'Rules you create appear here; none is scheduled right now.',
    subtitle: 'View and toggle maintenance rules',
    activeBanner: '{count} maintenance rule(s) currently active',
    idleBanner: 'No maintenance rules active',
    summary: '{active} active · {upcoming} upcoming · {total} total',
    statusActive: 'Active',
    statusInactive: 'Inactive',
    activate: 'Activate',
    deactivate: 'Deactivate',
    edit: 'Edit',
    delete: 'Delete',
    activated: 'Rule activated',
    deactivated: 'Rule deactivated',
    actionFailed: 'Action failed: check console for details',
    loadFailed: 'Failed to load maintenance state',
    enableGlobal: 'Enable Maintenance Mode',
    disableGlobal: 'Disable Maintenance Mode',
    enableGlobalConfirm: 'This will immediately block all user access to the platform. Staff can still log in and manage rules. Continue?',
    disableGlobalConfirm: 'This will immediately re-enable access for all users by clearing all active maintenance rules. Continue?',
    globalEnabled: 'Maintenance mode enabled',
    globalDisabled: 'Maintenance mode disabled',
    globalEnableFailed: 'Failed to enable maintenance mode',
    globalDisableFailed: 'Failed to disable maintenance mode',
    col: {
      target: 'Target',
      mode: 'Mode',
      reason: 'Reason',
      startsAt: 'Starts',
      endsAt: 'Ends',
      endsAtIndefinite: 'Indefinite',
      status: 'Status',
    },
    createRule: 'Create Rule',
    createRuleTitle: 'Create Maintenance Rule',
    createRuleSuccess: 'Maintenance rule created',
    createRuleFailed: 'Failed to create maintenance rule',
    editRuleTitle: 'Edit Maintenance Rule',
    updateRuleSuccess: 'Maintenance rule updated',
    updateRuleFailed: 'Failed to update maintenance rule',
    deleteRuleTitle: 'Delete Maintenance Rule',
    deleteRuleConfirm: 'This permanently deletes the rule. If it is active, this also lifts its effect. Continue?',
    deleteRuleSuccess: 'Maintenance rule deleted',
    deleteRuleFailed: 'Failed to delete maintenance rule',
    form: {
      target: 'Target',
      mode: 'Mode',
      scopeType: 'Scope',
      targetRole: 'Target role',
      regionMode: 'Region mode',
      regions: 'Regions',
      regionsHint: 'ISO country codes, e.g. NL, DE',
      module: 'Feature module',
      affectedEndpoints: 'Affected endpoints',
      affectedEndpointsHint: 'API paths, e.g. /v1/properties',
      priority: 'Priority',
      priorityHint: '0-1000; higher wins when rules overlap',
      internalNotes: 'Internal notes',
      message: 'Message',
      startsAt: 'Starts at',
      endsAt: 'Ends at (optional, leave blank for indefinite)',
    },
    regionMode: {
      include: 'Include regions',
      exclude: 'Exclude regions',
    },
    target: {
      public: 'Public users',
      staff: 'Staff only',
      both: 'Everyone',
      role: 'Role-specific',
    },
    mode: {
      blocked: 'Blocked (full outage)',
      read_only: 'Read-only',
      degraded: 'Degraded',
      throttled: 'Throttled',
    },
    scope: {
      global: 'Global',
      ui: 'User interface',
      si: 'Staff interface',
      feature: 'Feature',
      endpoint: 'API endpoint',
    },
  },
  error: {
    maintenance: {
      title: 'Under Maintenance',
      subtitle: 'We will be back soon',
      tryAgain: 'Try Again',
      goHome: 'Go Home',
      autoRetry: 'Auto retry in {time}',
      deploy: {
        title: 'Deploying an Update',
        subtitle: 'A new version is rolling out, back in a moment',
      },
    },
  },

  // Router meta + StaffPageHeader strings. Kept in a dedicated `staff.*`
  // namespace so the route-table titles are isolated from the per-page
  // copy keys (which use top-level domain names like `dashboard.*`,
  // `tickets.*`, etc.). Without these, vue-i18n falls back to the key
  notifications: {
    title: 'Notifications',
    defaultTitle: 'New notification',
    bell: {
      ariaLabel: 'Notifications',
      unreadAriaLabel: '{count} unread notifications',
      tooltip: 'Notifications',
    },
    panel: {
      title: 'Notifications',
      empty: 'All caught up',
      viewAll: 'View all',
      openNotification: 'Open notification: {title}',
      markAllRead: 'Mark all read',
      markAsRead: 'Mark read',
      markAsUnread: 'Mark unread',
      markAsReadError: 'Could not mark the notification as read. Please try again.',
      markAsUnreadError: 'Could not mark the notification as unread. Please try again.',
      deleteConfirm: {
        title: 'Delete this notification?',
        message: 'It will be removed from your list. This cannot be undone.',
      },
      delete: 'Delete',
      archive: 'Archive',
      unarchive: 'Restore',
      groupToday: 'Today',
      groupYesterday: 'Yesterday',
      groupOlder: 'Older',
    },
    index: {
      refresh: 'Refresh',
      markAllRead: 'Mark all as read',
      deleteAllRead: 'Delete all read',
      deleteAllReadConfirm: {
        title: 'Delete all read notifications?',
        message: 'Every notification you have already read will be permanently removed.',
      },
      filters: {
        all: 'All',
        unread: 'Unread',
        read: 'Read',
        category: 'Category',
        active: 'Active',
        archived: 'Archived',
        search: 'Search notifications',
      },
      empty: {
        all: 'No notifications yet',
        unread: 'No unread notifications',
        read: 'No read notifications',
        archived: 'No archived notifications',
      },
    },
    settings: {
      title: 'Notification preferences',
      generalTitle: 'General',
      frequency: {
        label: 'Delivery frequency',
        realtime: 'Real-time',
        daily: 'Daily digest',
        weekly: 'Weekly digest',
      },
      delivery: {
        label: 'Delivery methods',
        email: 'Email',
        push: 'Browser push',
        sms: 'SMS',
      },
      channels: {
        label: 'Categories',
        messages: 'Messages',
        tasks: 'Tasks',
        properties: 'Properties',
        projects: 'Projects',
        teams: 'Teams',
        schedules: 'Schedules',
        security: 'Security',
        system: 'System',
      },
      popups: {
        title: 'In-app popups',
        description:
          'Control how popup alerts appear inside the staff console when a notification arrives. Notifications always land in your notification list and the bell badge, even with popups off.',
        enabled: 'Show popups',
        preview: 'Show message preview',
        position: 'Position',
        positionTopLeft: 'Top left',
        positionTopRight: 'Top right',
        positionBottomLeft: 'Bottom left',
        positionBottomRight: 'Bottom right',
        duration: 'Duration',
        durationShort: 'Short (3 seconds)',
        durationNormal: 'Standard (5 seconds)',
        durationLong: 'Long (8 seconds)',
        durationPersistent: 'Until dismissed',
      },
      saved: 'Preferences saved',
      saveFailed: 'Failed to save preferences',
      matrix: {
        title: 'Per-notification controls',
        description: 'Fine-tune which channels each notification uses. These override the category defaults above.',
        channels: { push: 'Push', webPush: 'Web', email: 'Email' },
        categories: {
          messages: 'Messages',
          tasks: 'Tasks',
          properties: 'Properties',
          projects: 'Projects',
          teams: 'Teams',
          workspace: 'Workspace',
          schedules: 'Schedules',
          timeTracking: 'Time Tracking',
          billing: 'Billing',
          moderation: 'Moderation',
          geospatial: 'Location',
          system: 'System',
          support: 'Support',
          comments: 'Comments',
        },
        events: {
          new_message: 'New message',
          message_mention: 'Mentioned in a message',
          task_assigned: 'Task assigned',
          task_completed: 'Task completed',
          task_due_soon: 'Task due soon',
          task_overdue: 'Task overdue',
          task_comment: 'New task comment',
          property_updated: 'Property updated',
          property_assigned: 'Property assigned',
          project_updated: 'Project updated',
          project_assigned: 'Project assigned',
          team_invitation: 'Team invitation',
          team_member_joined: 'Team member joined',
          workspace_invitation: 'Workspace invitation',
          workspace_role_changed: 'Role changed',
          workspace_deletion_approved: 'Workspace deletion approved',
          workspace_deletion_rejected: 'Workspace deletion rejected',
          workspace_deletion_completed: 'Workspace deleted',
          workspace_unsuspended: 'Workspace reactivated',
          schedule_reminder: 'Schedule reminder',
          schedule_updated: 'Schedule updated',
          schedule_paused: 'Schedule paused',
          time_entries_submitted: 'Time entries submitted',
          time_entry_approved: 'Time entry approved',
          time_entry_rejected: 'Time entry rejected',
          time_tracking_reminder: 'Time tracking reminder',
          subscription_expiring: 'Subscription expiring',
          payment_failed: 'Payment failed',
          trial_ending: 'Trial ending',
          content_flagged_for_review: 'Content flagged for review',
          route_updated: 'Route updated',
          geofence_triggered: 'Geofence triggered',
          system_announcement: 'System announcement',
          media_rejected: 'Media rejected',
          ticket_reply: 'Ticket reply',
          ticket_status_changed: 'Ticket status changed',
          comment_added: 'Comment added',
        },
      },
    },
    categoryFilter: {
      messages: 'Messages',
      tasks: 'Tasks',
      properties: 'Properties',
      projects: 'Projects',
      teams: 'Teams',
      workspace: 'Workspace',
      schedules: 'Schedules',
      timeTracking: 'Time Tracking',
      billing: 'Billing',
      geospatial: 'Location',
      moderation: 'Moderation',
      system: 'System',
    },
  },

  // literal and the browser tab title / breadcrumb shows e.g.
  // "staff.pages.dashboard.title" verbatim.
  // Shared strings for the tenant-data oversight pages (read-only SI domain
  // pages). Per-domain sub-blocks carry that domain's search placeholder,
  // empty text, load-error, statuses and column labels.
  oversight: {
    viewWorkspace: 'View workspace',
    filteredByProperty: 'Filtered by property',
    filteredByProject: 'Filtered by project',
    columns: {
      workspace: 'Workspace',
      creator: 'Creator',
      created: 'Created',
      updated: 'Updated',
      status: 'Status',
      property: 'Property',
    },
    filters: {
      workspace: 'Workspace',
      billingEntity: 'Billing entity',
      status: 'Status',
      to: 'To',
    },
    properties: {
      searchPlaceholder: 'Search by name or reference',
      empty: 'No properties yet',
      loadFailed: 'Failed to load properties',
      statuses: {
        active: 'Active',
        inactive: 'Inactive',
        disabled: 'Disabled',
        archived: 'Archived',
      },
      columns: {
        name: 'Property',
        reference: 'Reference',
        category: 'Category',
        projects: 'Projects',
        tasks: 'Tasks',
      },
    },
    projects: {
      searchPlaceholder: 'Search projects',
      empty: 'No projects yet',
      loadFailed: 'Failed to load projects',
      columns: {
        name: 'Project',
        type: 'Type',
        tasks: 'Tasks',
      },
    },
    tasks: {
      searchPlaceholder: 'Search tasks',
      empty: 'No tasks yet',
      loadFailed: 'Failed to load tasks',
      columns: {
        title: 'Task',
        priority: 'Priority',
        dueDate: 'Due',
      },
    },
    teams: {
      searchPlaceholder: 'Search teams',
      empty: 'No teams yet',
      loadFailed: 'Failed to load teams',
      columns: {
        name: 'Team',
        members: 'Members',
      },
    },
    schedules: {
      searchPlaceholder: 'Search schedules',
      empty: 'No schedules yet',
      loadFailed: 'Failed to load schedules',
      columns: {
        name: 'Schedule',
        frequency: 'Frequency',
        nextRun: 'Next run',
      },
    },
    inventory: {
      searchPlaceholder: 'Search inventory',
      empty: 'No inventory items yet',
      loadFailed: 'Failed to load inventory',
      columns: {
        name: 'Item',
      },
    },
    timeTracking: {
      tabs: {
        entries: 'Entries',
        rates: 'Rates',
        costCodes: 'Cost codes',
        workSchedules: 'Work schedules',
        documents: 'Documents',
      },
      searchPlaceholder: 'Search time entries',
      empty: 'No time entries yet',
      loadFailed: 'Failed to load time entries',
      columns: {
        hourlyRate: 'Hourly rate',
        costRate: 'Cost rate',
        currency: 'Currency',
        active: 'Active',
        code: 'Code',
        name: 'Name',
        budget: 'Budget',
        kind: 'Kind',
        weeklyHours: 'Weekly hours',
        default: 'Default',
        documentNumber: 'Number',
        title: 'Title',
        period: 'Period',
        user: 'User',
        hours: 'Hours',
        started: 'Started',
      },
    },
    billing: {
      searchPlaceholder: 'Search by number',
      empty: 'No billing documents yet',
      loadFailed: 'Failed to load billing documents',
      typeInvoice: 'Invoice',
      typeCreditNote: 'Credit note',
      typePayers: 'Payers',
      columns: {
        payerType: 'Type',
        country: 'Country',
        currency: 'Currency',
        workspaces: 'Workspaces',
        members: 'Members',
        number: 'Number',
        type: 'Type',
        payer: 'Billing entity',
        amount: 'Amount',
      },
    },
  },
  staff: {
    navigation: {
      home: 'Home',
    },
    time: {
      justNow: 'Just now',
      minutesAgo: '{n} minute ago | {n} minutes ago',
      hoursAgo: '{n} hour ago | {n} hours ago',
      daysAgo: '{n} day ago | {n} days ago',
    },
    pages: {
      properties: { title: 'Properties' },
      projects: { title: 'Projects' },
      tasks: { title: 'Tasks' },
      teams: { title: 'Teams' },
      schedules: { title: 'Schedules' },
      inventory: { title: 'Inventory' },
      timeTracking: { title: 'Time Tracking' },
      billing: { title: 'Billing' },
      dashboard: { title: 'Dashboard' },
      tickets: { title: 'Tickets' },
      users: { title: 'Users' },
      workspaces: { title: 'Workspaces' },
      ads: { title: 'Advertisements' },
      marketingTemplates: { title: 'Email Templates' },
      advertisers: { title: 'Advertisers' },
      marketplaceLeads: { title: 'Marketplace Leads' },
      acquisition: { title: 'Acquisition' },
      audit: { title: 'Audit Logs' },
      activity: { title: 'Activity' },
      sentEmails: { title: 'Sent Emails' },
      clientLogs: { title: 'Client Logs' },
      clientLogPolicies: { title: 'Logging Policies' },
      conversations: {
        title: 'Messaging Oversight',
        detail: 'Conversation Details',
      },
      messageReports: { title: 'Message Reports' },
      deletionRequests: {
        title: 'Deletion Requests',
        detail: 'Deletion Request Details',
      },
      moderation: { title: 'Content Moderation' },
      featureFlags: { title: 'Feature Flags' },
      featureFlagsMetrics: { title: 'Feature Flag Metrics' },
      featureFlagsABTests: { title: 'A/B Tests' },
      maintenance: { title: 'Maintenance Mode' },
      settings: { title: 'System Settings' },
      supportChat: { title: 'Support Chat' },
      knowledgeBase: { title: 'Knowledge Base' },
      reviewQueue: { title: 'AI Answer Review' },
      draftReview: { title: 'Draft Review' },
      mlPredictions: { title: 'ML Prediction Review' },
      mlModels: { title: 'ML Models' },
      mlFeedback: { title: 'ML Feedback' },
      notificationMetrics: { title: 'Notification Metrics' },
    },
  },
  legalDocuments: {
    title: 'Legal Documents',
    empty: 'No legal documents found',
    versions: 'Versions',
    viewContent: 'View',
    effectiveFrom: 'Effective from',
    reconsentHint: 'Publishing a version starts a versioned re-consent rollout, which only takes effect once the re-consent feature is enabled.',
    badges: {
      active: 'Active',
      inactive: 'Inactive',
      material: 'Material',
      nonMaterial: 'Non-material',
      requiresConsent: 'Requires consent',
    },
    publish: {
      button: 'Publish version',
      title: 'Publish new version',
    },
    form: {
      language: 'Language',
      effectiveFrom: 'Effective from',
      title: 'Title',
      summary: 'Summary',
      content: 'Content',
      format: 'Format',
      isMaterial: 'Material change (triggers re-consent)',
      justification: 'Why this is not a material change',
    },
    notify: {
      loadError: 'Failed to load legal documents',
      publishSuccess: 'Version published',
      publishError: 'Failed to publish the version',
    },
  },
  contentHealth: {
    title: 'Content health',
    workerUnreachable: 'The knowledge base worker is unreachable; showing no data.',
    empty: 'No published knowledge articles yet.',
    columns: {
      language: 'Language',
      published: 'Published',
      stale: 'Stale translations',
    },
    totals: {
      published: 'Published articles',
      stale: 'Stale translations',
    },
    gaps: {
      title: 'Knowledge gaps',
      empty: 'No knowledge gaps detected.',
      columns: {
        query: 'Query',
        occurrences: 'Times asked',
        lastSeen: 'Last asked',
      },
    },
    notify: {
      error: 'Failed to load content health',
    },
  },
  draftReview: {
    workerUnreachable: 'The knowledge worker is unreachable; no drafts shown.',
    hint: 'Auto-generated drafts await approval before they go live.',
    empty: 'No drafts to review.',
    forkDiff: {
      title: 'Fork comparison',
      original: 'Current article',
      draft: 'Proposed draft',
      close: 'Close',
      loadFailed: 'Could not load the two article versions.',
    },
    columns: {
      title: 'Title',
      language: 'Language',
      source: 'Source',
    },
    actions: {
      approve: 'Approve',
      reject: 'Reject',
    },
    notify: {
      approved: 'Draft published.',
      rejected: 'Draft rejected.',
      error: 'Could not load or update the drafts.',
    },
  },
  reviewQueue: {
    workerUnreachable: 'The knowledge worker is unreachable; no review items shown.',
    empty: 'No answers to review.',
    filter: {
      label: 'Status',
      pending: 'Pending',
      resolved: 'Resolved',
      dismissed: 'Dismissed',
    },
    status: {
      pending: 'Pending',
      in_review: 'In review',
      resolved: 'Resolved',
      dismissed: 'Dismissed',
    },
    columns: {
      notes: 'What was wrong',
      flaggedAnswer: 'Flagged answer',
      status: 'Status',
      created: 'Flagged',
    },
    actions: {
      resolve: 'Resolve',
      dismiss: 'Dismiss',
    },
    notify: {
      resolved: 'Marked resolved.',
      dismissed: 'Dismissed.',
      error: 'Could not load or update the review queue.',
    },
    correct: {
      title: 'Correct the AI answer',
      open: 'Correct',
      flaggedHeading: 'Flagged answer',
      confidence: 'Confidence: {value}%',
      noFlaggedAnswer: 'The flagged answer is no longer available.',
      notesHeading: 'What was wrong',
      mode: {
        create: 'Write a new article',
        link: 'Link an existing article',
      },
      link: {
        label: 'Article',
        empty: 'No published articles to link.',
      },
      submit: 'Resolve with this fix',
      notify: {
        success: 'Answer corrected and the review was resolved.',
      },
    },
  },
  knowledgeBase: {
    workerUnreachable: 'The support-chat worker is unreachable, so the knowledge base cannot be listed right now.',
    asyncHint: 'Changes are processed by the AI worker and appear in the catalogue once it has re-indexed.',
    search: {
      title: 'Test retrieval',
      placeholder: 'Enter a question the AI might receive',
      action: 'Search',
      clear: 'Clear search',
      empty: 'No matching articles for that query.',
      error: 'Search failed. Please try again.',
    },
    createTitle: 'New article',
    editTitle: 'Edit article',
    actions: {
      create: 'New article',
      edit: 'Edit',
      reindex: 'Reindex',
      reindexAll: 'Reindex all',
      reindexAllConfirm: 'Re-embed every published article? This can take a while.',
      archive: 'Archive',
    },
    columns: {
      title: 'Title',
      category: 'Category',
      language: 'Language',
      status: 'Status',
      cited: 'Cited',
      updated: 'Updated',
    },
    filters: {
      language: 'Language',
      category: 'Category',
      allLanguages: 'All languages',
    },
    status: {
      draft: 'Draft',
      published: 'Published',
      archived: 'Archived',
    },
    form: {
      title: 'Title',
      content: 'Content',
      contentEditHint: 'Edit the content to update it. Clearing the field keeps the current content.',
      write: 'Write',
      preview: 'Preview',
      previewEmpty: 'Nothing to preview yet. Write some markdown in the Write tab.',
      language: 'Language',
      category: 'Category',
      tags: 'Tags',
      tagsHint: 'Comma-separated.',
      status: 'Status',
    },
    archiveConfirm: {
      title: 'Archive article',
      message: 'Archive "{title}"? It will stop being used by the AI assistant.',
    },
    notify: {
      loadError: 'Failed to load the knowledge base',
      saveError: 'The action could not be dispatched',
      dispatched: 'Change dispatched. It will appear once the worker re-indexes.',
      saved: 'Article saved. It will appear in the assistant once re-indexed.',
    },
  },
  supportChat: {
    searchPlaceholder: 'Search by user, email, or topic',
    tabs: {
      all: 'All',
      active: 'Active',
      waiting: 'Waiting',
      resolved: 'Resolved',
      closed: 'Closed',
    },
    stats: {
      openSessions: 'Open',
      resolved: 'Resolved',
      total: 'Total',
      aiResolution: 'AI Resolution',
      escalated: 'Escalated',
      csat: 'Avg CSAT',
      avgFirstResponse: 'Avg First Response',
      byLanguage: 'By Language',
      language: 'Language',
      sessions: 'Sessions',
    },
    card: {
      anonymousUser: 'Anonymous',
      msgs: 'msgs',
    },
    detail: {
      loadError: 'Failed to load session.',
      actionFailed: 'Action failed. Please try again.',
      sender: {
        ai: 'AI',
        agent: 'Agent',
        user: 'User',
      },
      flag: {
        action: 'Flag as faulty',
        title: 'Flag this answer',
        prompt: 'What was wrong with this answer?',
        success: 'Answer flagged for review.',
        error: 'Could not flag the answer.',
      },
      reply: {
        placeholder: 'Type your reply...',
        send: 'Send',
      },
      actions: {
        assign: 'Assign to me',
        resolve: 'Resolve',
        close: 'Close',
      },
      attachments: {
        title: 'Shared files',
        download: 'Download',
        downloadError: 'Could not download that file. Please try again.',
        upload: 'Attach files',
        uploadError: 'Could not upload that file. Please try again.',
      },
    },
    copilot: {
      title: 'Suggested knowledge-base articles',
      insert: 'Insert',
      untitled: 'Untitled article',
      insertError: 'Could not load that article. Please try again.',
    },
    quickReplies: {
      title: 'Quick replies',
      tooltip: 'Insert a quick reply',
      loading: 'Loading...',
      empty: 'No quick replies available.',
    },
    availability: {
      online: 'Online',
      busy: 'Busy',
      away: 'Away',
      offline: 'Offline',
    },
    realtime: {
      newEscalation: 'A user has requested a human agent.',
    },
  },
  mlModels: {
    title: 'ML Models',
    subtitle: 'Model registry: review versions and control which model serves each task',
    loadFailed: 'Failed to load ML models',
    empty: 'No model versions registered yet',
    filters: { status: 'Status', modelName: 'Model name' },
    statuses: { all: 'All statuses' },
    columns: {
      model: 'Model',
      status: 'Status',
      fileSize: 'Size',
      promotedAt: 'Promoted',
      createdAt: 'Registered',
      actions: 'Actions',
    },
    actions: { details: 'Details', promote: 'Promote', deprecate: 'Deprecate' },
    details: { artifactPath: 'Artifact path', accuracyMetrics: 'Accuracy metrics', manifest: 'Manifest' },
    promoteDialog: {
      title: 'Promote model version',
      message: 'Promote {model} to be the live model? Any currently promoted version of the same model is deprecated automatically.',
    },
    deprecateDialog: {
      title: 'Deprecate model version',
      message: 'Deprecate {model}? It will no longer be eligible to serve.',
    },
    notify: {
      promoted: 'Model promoted',
      deprecated: 'Model deprecated',
      promoteFailed: 'Failed to promote model',
      deprecateFailed: 'Failed to deprecate model',
    },
  },
  mlFeedback: {
    title: 'ML Feedback',
    subtitle: 'Raw feedback ledger from members and staff; feeds the training pipeline',
    loadFailed: 'Failed to load ML feedback',
    empty: 'No feedback recorded yet',
    filters: { action: 'Action', taskType: 'Task type', jobId: 'Job ID' },
    actionFilters: { all: 'All actions' },
    columns: {
      jobId: 'Job',
      taskType: 'Task type',
      action: 'Action',
      labels: 'Labels',
      model: 'Model',
      createdAt: 'Recorded',
    },
    actions: { record: 'Record feedback' },
    recordDialog: {
      title: 'Record feedback',
      hint: 'Manually log a labelling decision for the training pipeline.',
      jobId: 'Job ID',
      taskType: 'Task type',
      action: 'Action',
      originalLabel: 'Original label',
      correctedLabel: 'Corrected label',
      correctedLabelHint: 'Required when the action is relabel',
      submit: 'Record',
    },
    notify: { recorded: 'Feedback recorded', recordFailed: 'Failed to record feedback' },
  },
  notificationMetrics: {
    title: 'Notification Metrics',
    subtitle: 'Engagement feedback, channel performance, and gross notification counts',
    loadFailed: 'Failed to load notification metrics',
    counts: { total: 'Total notifications', unread: 'Unread', archived: 'Archived' },
    feedback: {
      title: 'Feedback actions',
      caption: '{total} events in the last {days} days',
      empty: 'No feedback events in the window',
      actions: {
        dismissed: 'Dismissed',
        opened: 'Opened',
        acted_on: 'Acted on',
        muted: 'Muted',
        snoozed: 'Snoozed',
      },
    },
  },
  mlPredictions: {
    title: 'ML Prediction Review',
    subtitle: 'Review flagged ML predictions and correct labels to improve model accuracy.',
    loadFailed: 'Failed to load predictions',
    empty: 'No predictions to review',
    filters: {
      status: 'Status',
      modelName: 'Model',
      confidenceBelow: 'Confidence below',
    },
    statuses: {
      all: 'All',
      pending_review: 'Pending review',
      accepted: 'Accepted',
      rejected: 'Rejected',
    },
    columns: {
      model: 'Model',
      taskType: 'Task',
      originalLabel: 'Predicted label',
      confidence: 'Confidence',
      correctedLabel: 'Corrected',
      createdAt: 'Flagged at',
      actions: 'Actions',
    },
    actions: {
      accept: 'Accept',
      reject: 'Reject',
    },
    reviewDialog: {
      acceptTitle: 'Accept prediction',
      rejectTitle: 'Reject prediction',
      correctedLabelLabel: 'Corrected label',
      correctedLabelHint: 'Leave blank if the prediction is correct as-is.',
      notesLabel: 'Notes',
      submit: 'Submit review',
    },
    notify: {
      reviewed: 'Review recorded.',
      reviewFailed: 'Could not record the review.',
    },
  },
};
